import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import {
  buildAssistantThreadPath,
  CLAUDIA_ATTACHMENT_ACCEPT_ATTRIBUTE,
  describeClaudiaAttachmentDropHint,
  parseAssistantThreadIdFromPath,
} from "@repo/shared";
import { useQuery } from "convex/react";
import { ArrowUp, CircleAlert, Maximize2, Plus, Sparkles, Square, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UnreadDot } from "@/components/ui/unread-dot";
import { useTabsState } from "@/contexts/tabs-context";
import { MessageRichTextInput } from "@/features/rich-text";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useFileDropTarget } from "@/hooks/use-file-drop-target";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import { trackClaudiaEvent } from "@/lib/analytics/domain";
import { cn } from "@/lib/utils";
import {
  useRightSidebarClaudia,
  useRightSidebarNavigation,
  useRightSidebarProject,
  useRightSidebarUi,
} from "../../contexts";
import { useAskClaudiaNewThreadIntent } from "../../hooks/use-ask-claudia-new-thread-intent";
import { useAssistantComposerControls } from "../../hooks/use-assistant-composer-controls";
import { useAssistantRuntimeActivity } from "../../hooks/use-assistant-runtime-activity";
import { useClaudiaAttachments } from "../../hooks/use-claudia-attachments";
import {
  resolvePendingIntentPrompt,
  useClaudiaComposerSubmission,
} from "../../hooks/use-claudia-composer-submission";
import { useClaudiaTranscriptAutoFollow } from "../../hooks/use-claudia-transcript-auto-follow";
import {
  resolveLivePromptMessageId,
  useClaudiaTranscriptItems,
} from "../../hooks/use-claudia-transcript-items";
import { useClaudiaTranscriptMessages } from "../../hooks/use-claudia-transcript-messages";
import { useClaudiaTranscriptScrollBack } from "../../hooks/use-claudia-transcript-scroll-back";
import { useMarkThreadRead } from "../../hooks/use-mark-thread-read";
import { useOptimisticTranscriptExchange } from "../../hooks/use-optimistic-transcript-exchange";
import { usePendingAssistantAskUser } from "../../hooks/use-pending-assistant-ask-user";
import { useProjectConversations } from "../../hooks/use-project-conversations";
import { CLAUDIA_PROMPT_CHAR_LIMIT, getAssistantErrorMessage } from "../../lib/ai-assistant-errors";
import { findInteractiveNextStepsMessageKey } from "../../lib/next-steps-replies";
import {
  readThreadScopedValue,
  type ThreadScopedValue,
  useThreadScopedSetter,
} from "../../lib/thread-scoped-state";
import type { AssistantConversationListItem, AssistantConversationStatus } from "../../types";
import { AssistantAskUserDock } from "../assistant-ask-user-dock";
import { AssistantComposerAttachmentsStrip } from "../assistant-composer-attachments-strip";
import { AssistantContextUsageBadge } from "../assistant-context-usage-badge";
import { AssistantTranscriptScrollBackRow } from "../assistant-transcript-scroll-back-row";
import { AssistantTranscriptScrollToBottomButton } from "../assistant-transcript-scroll-to-bottom-button";
import {
  AssistantTranscriptTimelineItemRow,
  getAssistantTimelineRenderKey,
} from "../assistant-transcript-timeline-item";
import {
  ClaudiaQuickReplyProvider,
  useClaudiaQuickReplyValue,
} from "../claudia-quick-reply-context";
import { ConversationList } from "../conversations/conversation-list";

type ClaudiaPanelTab = "chat" | "history";

const CONTEXTUAL_PANEL_PROFILE_ID = "contextual-panel-write";
const CONTEXTUAL_PANEL_SURFACE_ID = "sidebar-panel";

// ============================================================================
// useClaudiaThread — Thread state management for the panel
// ============================================================================

export function useClaudiaThread(projectId: Id<"projects">) {
  const {
    rememberedAssistantThreadId,
    setRememberedAssistantThread,
    clearRememberedAssistantThread,
    currentPath,
  } = useRightSidebarNavigation();
  const assistantApi = api.claudia;
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isLoadingThread, setIsLoadingThread] = useState(true);
  const rememberedThread = useQuery(
    assistantApi.getThreadById,
    rememberedAssistantThreadId ? { projectId, threadId: rememberedAssistantThreadId } : "skip"
  );
  const getThread = useQuery(
    assistantApi.getThread,
    threadId || (rememberedAssistantThreadId && rememberedThread !== null)
      ? "skip"
      : {
          projectId,
          profileId: CONTEXTUAL_PANEL_PROFILE_ID,
          surfaceId: CONTEXTUAL_PANEL_SURFACE_ID,
        }
  );
  const ensureThread = useObservedMutation(assistantApi.ensureThread, {
    feature: "assistant",
    operation: "ensure-thread",
    includeRequestId: true,
  });
  const initializationRef = useRef<symbol | null>(null);
  const previousProjectIdRef = useRef(projectId);

  const switchToThread = useCallback(
    (newThreadId: string, remember = true) => {
      initializationRef.current = null;
      setThreadId(newThreadId);
      setIsLoadingThread(false);
      if (remember) {
        setRememberedAssistantThread(newThreadId);
      }
    },
    [setRememberedAssistantThread]
  );

  const clearThread = useCallback(() => {
    initializationRef.current = null;
    setThreadId(null);
    setIsLoadingThread(true);
    clearRememberedAssistantThread();
  }, [clearRememberedAssistantThread]);

  useEffect(() => {
    if (previousProjectIdRef.current === projectId) {
      return;
    }

    previousProjectIdRef.current = projectId;
    initializationRef.current = null;
    setThreadId(null);
    setIsLoadingThread(true);
  }, [projectId]);

  // Effect 1 — Follow remembered assistant thread changes
  useEffect(() => {
    if (rememberedAssistantThreadId) {
      initializationRef.current = null;
      if (rememberedThread === undefined) {
        setIsLoadingThread(true);
        return;
      }
      if (rememberedThread) {
        setThreadId(rememberedAssistantThreadId);
        setIsLoadingThread(false);
        return;
      }
      clearThread();
    }
  }, [rememberedAssistantThreadId, rememberedThread, clearThread]);

  // Effect 2 — Guarded backfill: remember the panel thread when the route can defer to it
  useEffect(() => {
    if (threadId && !rememberedAssistantThreadId && !isAiAssistantRoute(currentPath)) {
      setRememberedAssistantThread(threadId);
    }
  }, [threadId, rememberedAssistantThreadId, currentPath, setRememberedAssistantThread]);

  // Effect 3 — Fallback on mount with no thread
  useEffect(() => {
    if (threadId || initializationRef.current !== null) return;

    if (rememberedAssistantThreadId || getThread === undefined) return;

    const shouldRememberFallbackThread = !isAiAssistantRoute(currentPath);

    if (getThread?.threadId) {
      switchToThread(getThread.threadId, shouldRememberFallbackThread);
      setIsLoadingThread(false);
      return;
    }

    // No threads exist — create one
    const initialization = Symbol();
    initializationRef.current = initialization;
    void ensureThread({
      projectId,
      profileId: CONTEXTUAL_PANEL_PROFILE_ID,
      surfaceId: CONTEXTUAL_PANEL_SURFACE_ID,
    })
      .then((result) => {
        if (initializationRef.current === initialization) {
          switchToThread(result.threadId, shouldRememberFallbackThread);
        }
      })
      .catch(() => {
        // intentional: ensureThread is observed, and the panel can recover when the user retries.
      })
      .finally(() => {
        if (initializationRef.current === initialization) {
          initializationRef.current = null;
          setIsLoadingThread(false);
        }
      });
    // An explicit selection, a new project or unmount revokes this request's right to
    // choose the conversation, even though the mutation itself may still finish.
    return () => {
      if (initializationRef.current === initialization) {
        initializationRef.current = null;
      }
    };
  }, [
    threadId,
    rememberedAssistantThreadId,
    getThread,
    ensureThread,
    projectId,
    switchToThread,
    currentPath,
  ]);

  return { threadId, switchToThread, clearThread, isLoadingThread };
}

function isAiAssistantRoute(path: string): boolean {
  return path === "/ai-assistant" || path.startsWith("/ai-assistant/");
}

// ============================================================================
// ClaudiaPanel
// ============================================================================

export function ClaudiaPanel() {
  const { projectId, tabId } = useRightSidebarProject();
  // Every open project keeps its layout — and therefore this panel — mounted behind a
  // `display: none` keep-alive host, so "am I rendered" is not "is anyone looking at me".
  // Without this a background tab whose conversation flipped to needs-you would clear its own
  // blue dot (LAT-990).
  const { activeTabId, activeWorkspaceKind } = useTabsState();
  const isPanelVisible = activeWorkspaceKind === "project" && activeTabId === tabId;
  const { toggleSidebar } = useRightSidebarUi();
  const { navigate, currentPath } = useRightSidebarNavigation();
  const { currentViewContext, pendingAskClaudiaIntent, clearAskClaudiaIntent } =
    useRightSidebarClaudia();
  const typedProjectId = projectId as Id<"projects">;
  const { threadId, switchToThread, clearThread, isLoadingThread } =
    useClaudiaThread(typedProjectId);
  const [activeTab, setActiveTab] = useState<ClaudiaPanelTab>("chat");
  const [error, setError] = useState<string | null>(null);
  // One subscription for both consumers on this surface: the Conversations list and the tab
  // label's dot. `useProjectConversations` is deliberately the only place either reads from,
  // so the dot can never disagree with the rows behind it.
  //
  // Skipped while this panel is not the visible one. Both consumers are *inside* the panel — a
  // hidden mount's tab dot and conversation rows are behind `display: none` — so every open
  // project was paying for a live list nobody could see, and the counts they feed are only ever
  // read on the way back in. `useQuery("skip")` re-subscribes on re-activation by itself, and
  // the read marker below already refuses to fire from a hidden mount, so an empty list here is
  // "unknown", never "idle".
  const conversations = useProjectConversations({
    projectId: isPanelVisible ? typedProjectId : null,
  });

  const assistantApi = api.claudia;
  const createNewThread = useObservedMutation(assistantApi.createNewThread, {
    feature: "assistant",
    operation: "create-new-thread",
    includeRequestId: true,
  });
  const cleanupEmptyThread = useObservedMutation(assistantApi.cleanupEmptyThread, {
    feature: "assistant",
    operation: "cleanup-empty-thread",
    includeRequestId: true,
    failureOutcome: "best_effort_failure",
  });
  const prevThreadIdRef = useRef<string | null>(null);

  useEffect(() => {
    trackClaudiaEvent("claudia_opened", {
      surface: "right_panel",
      project_id: typedProjectId,
    });
  }, [typedProjectId]);

  // A new ask-intent brings the Chat tab forward ONCE. This used to re-fire on every
  // `activeTab` change, which pinned the panel to Chat for as long as the intent was pending
  // — so the Conversations tab (the user's way out of a stuck intent, LAT-904 finding A) was
  // unreachable exactly when it was needed.
  const focusedIntentRequestKeyRef = useRef<number | null>(null);
  useEffect(() => {
    const requestKey = pendingAskClaudiaIntent?.requestKey ?? null;
    if (requestKey === null || focusedIntentRequestKeyRef.current === requestKey) {
      return;
    }
    focusedIntentRequestKeyRef.current = requestKey;
    setActiveTab("chat");
  }, [pendingAskClaudiaIntent]);

  // Blank-thread cleanup on thread switch
  useEffect(() => {
    const prevId = prevThreadIdRef.current;
    if (prevId && prevId !== threadId) {
      void cleanupEmptyThread({
        projectId: typedProjectId,
        threadId: prevId,
        expectedSurfaceId: CONTEXTUAL_PANEL_SURFACE_ID,
        source: "sidebar-panel.thread-switch",
      }).catch(() => {
        // intentional: blank-thread cleanup is best-effort during local thread switching.
      });
    }
    prevThreadIdRef.current = threadId;
  }, [threadId, cleanupEmptyThread, typedProjectId]);

  // Creates a fresh panel thread WITHOUT adopting it. Splitting create from adopt is what
  // lets the intent chain re-check its generation between the two (LAT-904); the "New chat"
  // button just calls both in a row.
  const createThread = useCallback(async () => {
    // Clean up current blank thread before creating new one
    setError(null);
    if (threadId) {
      void cleanupEmptyThread({
        projectId: typedProjectId,
        threadId,
        expectedSurfaceId: CONTEXTUAL_PANEL_SURFACE_ID,
        source: "sidebar-panel.create-before-switch",
      }).catch(() => {
        // intentional: blank-thread cleanup must not block starting a new local thread.
      });
    }
    const result = await createNewThread({
      projectId: typedProjectId,
      profileId: CONTEXTUAL_PANEL_PROFILE_ID,
      surfaceId: CONTEXTUAL_PANEL_SURFACE_ID,
    });
    return result.threadId;
  }, [threadId, cleanupEmptyThread, createNewThread, typedProjectId]);

  const adoptThread = useCallback(
    (createdThreadId: string) => {
      switchToThread(createdThreadId);
      trackClaudiaEvent("claudia_thread_created", {
        surface: "right_panel",
        project_id: typedProjectId,
        thread_id: createdThreadId,
      });
      setActiveTab("chat");
    },
    [switchToThread, typedProjectId]
  );

  const cleanupSupersededThread = useCallback(
    (supersededThreadId: string) => {
      void cleanupEmptyThread({
        projectId: typedProjectId,
        threadId: supersededThreadId,
        expectedSurfaceId: CONTEXTUAL_PANEL_SURFACE_ID,
        source: "sidebar-panel.superseded-intent",
      }).catch(() => {
        // intentional: cleaning up a superseded blank thread is best-effort.
      });
    },
    [cleanupEmptyThread, typedProjectId]
  );

  const handleNewChat = useCallback(async () => {
    // Starting a chat by hand abandons a pending ask-intent: its prompt belongs to the
    // thread it asked for, not to this one, and leaving it pending would keep Send gated.
    clearAskClaudiaIntent();
    try {
      adoptThread(await createThread());
    } catch (cause: unknown) {
      setError(getAssistantErrorMessage(cause, "Failed to create chat"));
    }
  }, [adoptThread, clearAskClaudiaIntent, createThread]);

  const handleNewThreadIntentError = useCallback((cause: unknown) => {
    setError(getAssistantErrorMessage(cause, "Failed to start a new chat"));
  }, []);

  // LAT-620/LAT-904: when an ask-intent requests a fresh thread (the QG "Fix with Claudia"
  // button), open one exactly once per requestKey and gate Send until it is active.
  const { blockSend: blockSendUntilNewThreadReady } = useAskClaudiaNewThreadIntent({
    intent: pendingAskClaudiaIntent,
    activeThreadId: threadId,
    createThread,
    adoptThread,
    cleanupSupersededThread,
    clearIntent: clearAskClaudiaIntent,
    onError: handleNewThreadIntentError,
  });

  const handleHistoryClick = useCallback(
    (clickedThreadId: string) => {
      // Picking another conversation abandons a pending ask-intent (LAT-904 finding A):
      // without this, `blockSend` stayed true in every thread the user switched to.
      clearAskClaudiaIntent();
      setError(null);
      switchToThread(clickedThreadId);
      setActiveTab("chat");
    },
    [clearAskClaudiaIntent, switchToThread]
  );

  const handleExpand = useCallback(() => {
    if (activeTab === "history") {
      navigate("/ai-assistant/history");
    } else if (threadId) {
      navigate(buildAssistantThreadPath(threadId));
    }
  }, [activeTab, threadId, navigate]);

  // LAT-880 double mount: the full-screen assistant route keeps this panel mounted (inert, and
  // collapsed to zero width by `ProjectLayout`) while it shows the same thread. Both would
  // otherwise hold the same subscriptions open, so the panel pauses its transcript and runtime
  // queries — the remembered thread is untouched and the panel resumes the moment the route no
  // longer covers it.
  const subscribedThreadId =
    threadId && parseAssistantThreadIdFromPath(currentPath) === threadId ? null : threadId;
  // LAT-950: resume by remounting the chat, not by handing the same hook instance its thread
  // back. The vendored `@convex-dev/agent` hooks keep a per-component stream cache that is only
  // reset on a real→real thread change, so a pause (which routes their subscription to "skip")
  // leaves the paused thread's rows in that cache, to be rebuilt with no thread of their own and
  // merged into whatever is on screen next. A fresh mount is the only way to clear it. Keyed on
  // the pause state alone: thread switches are handled correctly by those hooks, and remounting
  // for one would throw away a draft the user is still writing.
  const chatSubscriptionKey = threadId && !subscribedThreadId ? "chat:paused" : "chat:active";
  // The open thread's status, read off the list this panel already subscribes to rather than a
  // per-thread runtime snapshot (D6/LAT-966). `useMarkThreadRead` needs the *edge* into
  // needs-you, which is why the status travels with the thread id instead of being re-derived.
  const openConversationStatus = resolveConversationStatus(conversations.items, threadId);

  return (
    <div className="flex h-full flex-col bg-surface-raised">
      {/* Custom header */}
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-border px-3">
        <div className="flex items-center gap-1.5">
          <div className="flex size-5 items-center justify-center bg-background dark:bg-muted/30">
            <Sparkles className="size-3 text-primary" />
          </div>
          <h2 className="text-sm font-semibold text-foreground">Claudia</h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleExpand}
            aria-label="Expand to full screen"
            disabled={activeTab === "chat" && !threadId}
            className="flex size-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <Maximize2 className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Close panel"
            className="flex size-6 items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>

      {/*
        The panel's own top level, so the strip is `variant="primary"` per the Tabs depth rule
        (`.design-sync/conventions.md`) — this used to be a hand-rolled inset-shadow underline.
        "New Chat" stays an action button beside the strip rather than a third tab: it starts
        something, it does not switch between two things that are already there.
      */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as ClaudiaPanelTab)}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        {/*
          The strip's height comes from the list (`h-9`), not from this row: the row also owns
          the 1px rule the active trigger's underline sits on, and pinning it to `h-9` too would
          eat that pixel out of the list's own height.
        */}
        <div className="flex shrink-0 items-center border-b border-border pr-1.5">
          <TabsList variant="primary" className="w-auto border-b-0">
            <TabsTrigger value="chat">Chat</TabsTrigger>
            <TabsTrigger value="history">
              Conversations
              {conversations.needsYouCount > 0 ? (
                <span data-testid="claudia-panel-needs-you-dot" className="flex items-center">
                  <UnreadDot active />
                </span>
              ) : null}
            </TabsTrigger>
          </TabsList>
          <button
            type="button"
            className="ml-auto flex h-9 items-center justify-center gap-1.5 px-2.5 text-xs text-neutral-dim transition-colors hover:text-foreground"
            onClick={() => void handleNewChat()}
          >
            <Plus className="size-3" />
            New Chat
          </button>
        </div>

        {currentViewContext ? (
          <div className="border-b border-border px-3.5 py-2 text-[11px] text-muted-foreground">
            Context: {describePanelContext(currentViewContext)}
          </div>
        ) : null}

        {error ? (
          <p className="border-b border-border px-3.5 py-2 text-xs text-destructive">{error}</p>
        ) : null}

        {/* Content area — Base UI unmounts the inactive panel, so only one tab subscribes. */}
        <TabsContent value="chat" className="flex min-h-0 flex-1 flex-col">
          <ChatTabContent
            key={chatSubscriptionKey}
            projectId={typedProjectId}
            threadId={threadId}
            subscribedThreadId={subscribedThreadId}
            conversationStatus={openConversationStatus}
            isPanelVisible={isPanelVisible}
            isLoadingThread={isLoadingThread}
            currentViewContext={currentViewContext}
            pendingAskClaudiaIntent={pendingAskClaudiaIntent}
            blockSend={blockSendUntilNewThreadReady}
            onThreadUnavailable={() => {
              clearThread();
              setError("Conversation no longer available. Loading another chat.");
            }}
          />
        </TabsContent>

        <TabsContent value="history" className="flex min-h-0 flex-1 flex-col">
          <ConversationsTabContent
            items={conversations.items}
            isLoading={conversations.isLoading}
            currentThreadId={threadId}
            onSelect={handleHistoryClick}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** The open thread's projected status, or `null` when the list has not placed it yet. */
function resolveConversationStatus(
  items: readonly AssistantConversationListItem[],
  threadId: string | null
): AssistantConversationStatus | null {
  if (!threadId) {
    return null;
  }
  return items.find((item) => item.threadId === threadId)?.status ?? null;
}

// ============================================================================
// ChatTabContent
// ============================================================================

function ChatTabContent({
  projectId,
  threadId,
  subscribedThreadId,
  conversationStatus,
  isPanelVisible,
  isLoadingThread,
  currentViewContext,
  pendingAskClaudiaIntent,
  blockSend = false,
  onThreadUnavailable,
}: {
  projectId: Id<"projects">;
  threadId: string | null;
  /**
   * The thread this mount subscribes to — `null` while the full-screen route covers the same
   * thread. Owned by the panel, which also remounts this component when it flips (LAT-950).
   */
  subscribedThreadId: string | null;
  /** The open thread's projected status, from the panel's one conversations subscription. */
  conversationStatus: AssistantConversationStatus | null;
  /** Whether this panel's project tab is the one on screen — see `ClaudiaPanel`. */
  isPanelVisible: boolean;
  isLoadingThread: boolean;
  currentViewContext: ReturnType<typeof useRightSidebarClaudia>["currentViewContext"];
  pendingAskClaudiaIntent: ReturnType<typeof useRightSidebarClaudia>["pendingAskClaudiaIntent"];
  /** LAT-620: block sending until a requested new thread is active (steering race guard). */
  blockSend?: boolean;
  onThreadUnavailable: () => void;
}) {
  const assistantApi = api.claudia;
  const currentUser = useCurrentUser();
  const { clearAskClaudiaIntent } = useRightSidebarClaudia();
  const sendMessage = useObservedMutation(assistantApi.submitMessage, {
    feature: "assistant",
    operation: "submit-message",
    includeRequestId: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // LAT-990: the dock switches threads without remounting (the chat is keyed on the pause state,
  // not the thread, so a switch does not throw away a draft someone is writing), so the prompt and
  // run this mount is tracking have to name the thread they belong to. Read back under any other
  // thread they are simply absent — see `lib/thread-scoped-state.ts`.
  const [latestPrompt, setLatestPrompt] = useState<ThreadScopedValue | null>(null);
  const [currentRun, setCurrentRun] = useState<ThreadScopedValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const threadIdRef = useRef<string | null>(threadId);
  threadIdRef.current = threadId;
  const latestPromptMessageId = readThreadScopedValue(latestPrompt, threadId);
  const currentRunId = readThreadScopedValue(currentRun, threadId);
  const setLatestPromptMessageId = useThreadScopedSetter(setLatestPrompt, threadIdRef);
  const setCurrentRunId = useThreadScopedSetter(setCurrentRun, threadIdRef);
  // Everything else this mount holds about the open conversation — which activity sections are
  // expanded, the last send failure — is display state with no thread of its own, so it is cleared
  // on a real switch instead. The ref is written only here: reading one assigned during render
  // would compare the new thread against itself (LAT-990).
  const shownThreadIdRef = useRef<string | null>(threadId);
  useEffect(() => {
    if (shownThreadIdRef.current === threadId) {
      return;
    }
    shownThreadIdRef.current = threadId;
    setError(null);
  }, [threadId]);
  const optimisticExchange = useOptimisticTranscriptExchange({
    projectId,
    threadId,
  });
  const {
    exchange: optimisticTranscriptExchange,
    startExchange,
    resolveExchange,
    clearExchange,
  } = optimisticExchange;

  // LAT-880: liveness must be resolved before the transcript subscriptions, so the message feed
  // is subscribed here and handed to `useClaudiaTranscriptItems` (settled/live window split).
  const messageFeed = useClaudiaTranscriptMessages({
    projectId,
    threadId: subscribedThreadId,
  });
  // Thread-filtered: the vendored feed keeps rows from the thread this dock was showing before
  // (LAT-950), and this is the live window's fallback anchor (LAT-990).
  const latestVisiblePromptMessage = [...messageFeed.results]
    .reverse()
    .find((message) => message.role === "user" && message.threadId === subscribedThreadId);
  const latestVisiblePromptMessageId =
    latestVisiblePromptMessage?._id ?? latestVisiblePromptMessage?.id ?? null;
  const activePromptMessageId = latestPromptMessageId ?? latestVisiblePromptMessageId;
  const runtimeActivity = useAssistantRuntimeActivity({
    projectId,
    threadId: subscribedThreadId,
    latestPromptMessageId: activePromptMessageId,
    currentRunId,
  });
  const transcriptFeed = useClaudiaTranscriptItems({
    projectId,
    threadId: subscribedThreadId,
    messageFeed,
    livePromptMessageId: resolveLivePromptMessageId({
      runtimeRun: runtimeActivity.runtimeRun,
      promptExecutionStatus: runtimeActivity.promptExecutionStatus,
      latestPromptMessageId: activePromptMessageId,
    }),
    optimisticExchange: optimisticTranscriptExchange,
  });
  const messageResults = transcriptFeed.messages;
  const timelineItems = transcriptFeed.results;
  const narration = transcriptFeed.narration;
  const hasActiveAssistantMessage = transcriptFeed.hasActiveAssistantMessage;
  const messageStatus = transcriptFeed.status;
  const isLoadingMessages = messageStatus === undefined || messageStatus === "LoadingFirstPage";
  const showEmptyState = !isLoadingThread && messageResults.length === 0 && !isLoadingMessages;
  const attachmentDraft = useClaudiaAttachments({
    projectId,
    threadId,
    disabled: !threadId || blockSend,
  });
  const optimisticAttachments = attachmentDraft.attachments
    .filter((attachment) => attachment.status === "ready" && attachment.attachmentId)
    .map((attachment) => ({
      type: "attachment" as const,
      attachmentId: String(attachment.attachmentId),
      kind: attachment.kind,
      fileName: attachment.fileName,
      mimeType: attachment.mimeType,
      fileSizeBytes: attachment.fileSizeBytes,
      width: attachment.width,
      height: attachment.height,
      url: attachment.previewUrl,
      thumbnailUrl: attachment.previewUrl,
    }));
  const handleComposerError = useCallback((message: string) => {
    setError(message);
  }, []);
  const composerControls = useAssistantComposerControls({
    projectId,
    threadId,
    isSubmittingMessage: isSubmitting,
    hasPendingOptimisticExchange: Boolean(
      optimisticTranscriptExchange && !optimisticTranscriptExchange.promptMessageId
    ),
    latestPromptMessageId: activePromptMessageId,
    promptExecutionStatus: runtimeActivity.promptExecutionStatus,
    runtimeRun: runtimeActivity.runtimeRun,
    onError: handleComposerError,
  });
  const askUserStop =
    composerControls.mode === "stop" || composerControls.mode === "stopping"
      ? {
          onStop: () => void composerControls.handleStop(),
          disabled: composerControls.isStopDisabled,
          loading: composerControls.mode === "stopping",
        }
      : undefined;
  const currentThread = useQuery(
    assistantApi.getThreadById,
    threadId ? { projectId, threadId } : "skip"
  );
  const pendingAskUser = usePendingAssistantAskUser({
    projectId,
    threadId,
  });
  // Clears this conversation's blue dot. Keyed on `subscribedThreadId`, not `threadId`: while
  // the full-screen route covers the same thread this mount is paused and that surface is the
  // one being read, so a paused panel must not mark anything (LAT-880 double mount).
  useMarkThreadRead({
    projectId,
    threadId: subscribedThreadId,
    status: conversationStatus,
    enabled: isPanelVisible,
  });
  // Only this mount of the dock gets the preview affordance: it is the one that sits beside an
  // open editor, whose Versions panel the comparison drives.
  // The whole panel chat area (transcript + composer) is one file drop target.
  const attachmentDrop = useFileDropTarget({
    enabled: Boolean(threadId) && !blockSend && !pendingAskUser,
    accept: CLAUDIA_ATTACHMENT_ACCEPT_ATTRIBUTE,
    onFiles: attachmentDraft.addFiles,
  });
  const promptHistoryUserId = currentUser?._id ?? currentThread?.userId ?? null;
  // LAT-952: the same scroll-back the full-screen timeline uses. A paused mount subscribes to
  // nothing, so its feed is empty and there is nothing to page back through until it resumes —
  // and resuming remounts the chat, which restores to the bottom of a fresh first page.
  const scrollBack = useClaudiaTranscriptScrollBack({
    canLoadMore: transcriptFeed.canLoadMore,
    isLoadingMore: transcriptFeed.isLoadingMore,
    firstItemKey: timelineItems[0] ? getAssistantTimelineRenderKey(timelineItems[0]) : null,
    loadMore: transcriptFeed.loadMore,
  });
  // LAT-963: follows the stream only while the reader is still at the end. Scroll-back's ref for
  // the same viewport is composed in, so the node carries one ref rather than two.
  const autoFollow = useClaudiaTranscriptAutoFollow({
    items: timelineItems,
    narration,
    viewportRef: scrollBack.viewportRef,
    followKey: threadId,
  });

  // One gate for the send action, read by both `handleSend` and the composer's button. They
  // must not diverge: the composer becomes usable the moment the fresh thread's query
  // resolves, which races the attach-artifacts mutation on the same thread, and an enabled
  // button whose click `handleSend` silently refuses restores the draft and looks like
  // nothing happened (uat-e2e 32185862959, 3/3 attempts). Text stays editable throughout.
  const canSubmitPrompt =
    (composerControls.mode === "send" || composerControls.mode === "stop") && !blockSend;
  const { handleSend, handleVoiceTranscriptionCreated, promptHistoryEntries } =
    useClaudiaComposerSubmission({
      projectId,
      threadId,
      surface: "right_panel",
      promptHistoryUserId,
      isSubmitting,
      canSubmitPrompt,
      attachmentIds: attachmentDraft.attachmentIds,
      optimisticAttachments,
      hasAttachments: attachmentDraft.hasAttachments,
      isUploadingAttachments: attachmentDraft.isUploading,
      resetAttachments: attachmentDraft.reset,
      setAttachmentsSubmissionInFlight: attachmentDraft.setSubmissionInFlight,
      currentViewContext,
      pendingAskClaudiaIntent,
      clearAskClaudiaIntent,
      timelineItems,
      runtimeActivity,
      sendMessage,
      startExchange,
      resolveExchange,
      clearExchange,
      setIsSubmitting,
      setError,
      setCurrentRunId,
      setLatestPromptMessageId,
      onThreadUnavailable,
    });
  // The panel owns the composer and the transcript, so it publishes the send straight down.
  const quickReply = useClaudiaQuickReplyValue(
    handleSend,
    composerControls.mode === "send" &&
      canSubmitPrompt &&
      !isSubmitting &&
      !attachmentDraft.isUploading,
    threadId
  );
  const interactiveNextStepsKey = useMemo(
    () => findInteractiveNextStepsMessageKey(timelineItems),
    [timelineItems]
  );

  // LAT-935: auto-submit. A "Fix with Claudia" click used to leave a filled composer waiting for
  // Send — Galina's chats were full of drafts nobody ever sent. The intent now submits itself the
  // moment its thread is genuinely ready.
  //
  // Sequencing is the whole difficulty. `blockSend` is false only once the fresh thread has been
  // created AND adopted as the active one (LAT-904), so this cannot be an on-mount effect: firing
  // earlier submits into the *previous* thread. The `requestKey` ledger below makes the send
  // exactly-once per intent even though the effect re-runs on every dependency change, and it is
  // never reset on failure — a failed auto-submit leaves the text in the composer for the user to
  // send manually rather than retrying itself in a loop.
  const autoSubmittedRequestKeyRef = useRef<number | null>(null);
  useEffect(() => {
    const intent = pendingAskClaudiaIntent;
    if (!intent?.autoSubmit || !threadId || blockSend || !canSubmitPrompt || isSubmitting) {
      return;
    }
    if (autoSubmittedRequestKeyRef.current === intent.requestKey) {
      return;
    }
    autoSubmittedRequestKeyRef.current = intent.requestKey;
    const markdown = resolvePendingIntentPrompt(intent);
    if (!markdown.trim()) {
      return;
    }
    void handleSend({ markdown, plainText: markdown });
  }, [blockSend, canSubmitPrompt, handleSend, isSubmitting, pendingAskClaudiaIntent, threadId]);

  if (isLoadingThread) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-xs text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const emptyTitle = "Ask Claudia";
  const emptySubtitle = currentViewContext
    ? "Claudia can work from what you are looking at, while still using the rest of the project as context."
    : "Create, update, or analyze elements. Check gaps, validate logic, or get guidance.";
  const inputPlaceholder = pendingAskClaudiaIntent
    ? "Ask Claudia about this section..."
    : "Ask Claudia...";
  return (
    <div className="relative flex min-h-0 flex-1 flex-col" {...attachmentDrop.dropTargetProps}>
      {showEmptyState ? (
        <div className="flex flex-1 items-center justify-center bg-white/50 p-6 dark:bg-transparent">
          <div className="flex flex-col items-center gap-4">
            <div className="flex size-11 items-center justify-center bg-background dark:bg-muted/30">
              <Sparkles className="size-5 text-primary" />
            </div>
            <div className="flex flex-col items-center gap-1.5 text-center">
              <p className="text-[15px] font-semibold text-foreground">{emptyTitle}</p>
              <p className="text-xs leading-relaxed text-muted-foreground">{emptySubtitle}</p>
            </div>
          </div>
        </div>
      ) : (
        // `min-h-40` is the transcript's floor: while a question is pending the dock below
        // shrinks to fit and scrolls its answers, and it may take everything above this line.
        <div className="relative flex min-h-40 flex-1 flex-col">
          <ScrollShadow
            className="min-h-0 flex-1 bg-white/50 dark:bg-transparent"
            colorScheme="default"
            viewportRef={autoFollow.viewportRef}
            viewportProps={{ "data-testid": "assistant-transcript-viewport" }}
          >
            <div
              ref={autoFollow.contentRef}
              className="ph-no-capture flex w-full flex-col gap-5 p-4"
            >
              <AssistantTranscriptScrollBackRow
                sentinelRef={scrollBack.sentinelRef}
                canLoadMore={transcriptFeed.canLoadMore}
                isLoading={scrollBack.isLoadingOlderMessages}
                compact
              />
              {error ? (
                <Alert variant="destructive">
                  <CircleAlert />
                  <AlertTitle>{error}</AlertTitle>
                </Alert>
              ) : null}
              <ClaudiaQuickReplyProvider value={quickReply}>
                {timelineItems.map((item) => (
                  <AssistantTranscriptTimelineItemRow
                    key={getAssistantTimelineRenderKey(item)}
                    item={item}
                    projectId={projectId}
                    threadId={threadId}
                    narration={narration}
                    hasActiveAssistantMessage={hasActiveAssistantMessage}
                    interactiveNextSteps={item.key === interactiveNextStepsKey}
                    compact
                  />
                ))}
              </ClaudiaQuickReplyProvider>
            </div>
          </ScrollShadow>
          {autoFollow.isAtBottom ? null : (
            <AssistantTranscriptScrollToBottomButton onClick={autoFollow.scrollToBottom} />
          )}
        </div>
      )}

      {/* Input bar */}
      {pendingAskUser ? (
        <AssistantAskUserDock
          projectId={projectId}
          threadId={subscribedThreadId}
          request={pendingAskUser}
          compact
          voiceInput={threadId ? { projectId, threadId, source: "ask-user" } : undefined}
          stop={askUserStop}
          onError={setError}
        />
      ) : (
        <div className="relative z-20 border-t border-border px-3.5 py-2.5">
          <MessageRichTextInput
            // Remount the composer per ask-intent so a pre-filled prompt (e.g. "Fix with
            // Claudia") is actually seeded as the editor's initial content.
            // `resetKey` is intentionally omitted: it clears the editor, which would wipe
            // the seeded prompt; the fresh mount handles per-intent reset, and
            // `clearOnSubmitStart` handles the post-send clear.
            key={`composer:${pendingAskClaudiaIntent?.requestKey ?? "blank"}`}
            placeholder={inputPlaceholder}
            disabled={!threadId}
            // The send gate is `submitDisabled`, never `disabled` (which would
            // also freeze attachments/voice) and never `allowSubmitWithPrimaryAction`/
            // `primaryAction` (`canUseSubmitAction` is in the `useEditor` deps, so toggling it
            // rebuilds the editor and throws away what was typed while the thread was made).
            submitDisabled={!canSubmitPrompt}
            footerHint={blockSend ? "Preparing your chat…" : undefined}
            isSubmitting={isSubmitting}
            onSubmit={handleSend}
            clearOnSubmitStart
            initialMarkdown={resolvePendingIntentPrompt(pendingAskClaudiaIntent)}
            submitAriaLabel="Send prompt"
            submitIcon={<ArrowUp className="size-4" />}
            allowSubmitWithPrimaryAction={composerControls.mode !== "send"}
            primaryAction={
              composerControls.mode !== "send"
                ? {
                    mode: "button",
                    ariaLabel:
                      composerControls.mode === "stopping"
                        ? "Stopping assistant response"
                        : "Stop assistant response",
                    icon: <Square className="size-3.5 fill-current" />,
                    disabled: composerControls.isStopDisabled,
                    loading: composerControls.mode === "stopping",
                    onClick: () => void composerControls.handleStop(),
                  }
                : undefined
            }
            voiceInput={
              threadId
                ? {
                    projectId,
                    threadId,
                    source: "claudia-composer",
                    surface: "right_panel",
                    onTranscriptionCreated: handleVoiceTranscriptionCreated,
                  }
                : undefined
            }
            attachments={{
              accept: CLAUDIA_ATTACHMENT_ACCEPT_ATTRIBUTE,
              disabled: !threadId || blockSend,
              hasReadyAttachments: attachmentDraft.attachmentIds.length > 0,
              hasPendingAttachments: attachmentDraft.isUploading,
              dropHint: describeClaudiaAttachmentDropHint(),
              dropActive: attachmentDrop.isActive,
              onFilesSelected: attachmentDraft.addFiles,
              attachmentsStrip: attachmentDraft.hasAttachments ? (
                <AssistantComposerAttachmentsStrip
                  attachments={attachmentDraft.attachments}
                  onRemove={(localId) => void attachmentDraft.removeAttachment(localId)}
                />
              ) : undefined,
            }}
            mentionProjectId={projectId}
            historyNavigation={{ entries: promptHistoryEntries }}
            characterLimit={{ limit: CLAUDIA_PROMPT_CHAR_LIMIT, warningThreshold: 0.9 }}
            toolbarPreset="none"
            composerLayout
            attachMenu
            showFooter
            contextIndicator={
              <AssistantContextUsageBadge usage={runtimeActivity.contextUsage} compact />
            }
            className="border-neutral-muted bg-white shadow-none dark:border-border dark:bg-background"
          />
        </div>
      )}
      {/* Gray wash over the transcript while dragging files; the composer (z-20) stays clear. */}
      <div
        className={cn(
          "pointer-events-none absolute inset-0 z-10 bg-foreground/[0.06] transition-opacity duration-200",
          attachmentDrop.isActive ? "opacity-100" : "opacity-0"
        )}
      />
    </div>
  );
}

// ============================================================================
// ConversationsTabContent
// ============================================================================

/**
 * The panel's Conversations tab: the shared `ConversationList` in its dense scale, and nothing
 * else. The rows arrive in the backend's order — pinned first, then activity descending — and
 * are rendered in it. The tab used to re-sort by `lastActivityAt` and keep the first 20, which
 * threw the pinned-first order away and silently hid the 21st conversation; the list on the
 * flyout and the one here would then disagree about the same project.
 */
function ConversationsTabContent({
  items,
  isLoading,
  currentThreadId,
  onSelect,
}: {
  items: readonly AssistantConversationListItem[];
  isLoading: boolean;
  currentThreadId: string | null;
  onSelect: (threadId: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-xs text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white/50 dark:bg-transparent">
      <ScrollShadow className="min-h-0 flex-1" colorScheme="default">
        <ConversationList
          items={items}
          currentThreadId={currentThreadId}
          density="dense"
          onSelect={onSelect}
        />
      </ScrollShadow>
    </div>
  );
}

function describePanelContext(
  context: NonNullable<ReturnType<typeof useRightSidebarClaudia>["currentViewContext"]>
) {
  switch (context.kind) {
    case "project":
      return context.label?.trim() || "this project";
    case "entity-tab":
      return context.tabLabel?.trim()
        ? `${context.entityName} · ${context.tabLabel}`
        : context.entityName;
    case "entity-section":
      return `${context.entityName} · ${context.fieldLabel}`;
    case "folder":
      return `Folder: ${context.label}`;
    case "collection":
      return context.parentFolderLabel?.trim()
        ? `Collection: ${context.label} in ${context.parentFolderLabel}`
        : `Collection: ${context.label}`;
    default:
      return "this context";
  }
}
